import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { 
  Briefcase, Calendar, User, FileText, Building2, X, Eye, Download,
  CheckCircle, ClipboardCheck, ArrowDownCircle, ArrowUpCircle, Receipt,
  ListChecks, AlertTriangle, ArrowUp, ArrowDown
} from 'lucide-react';

const JobDetailPopup = ({ jobId, isOpen, onClose }) => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && jobId) {
      fetchJobDetails();
    }
  }, [isOpen, jobId]);

  const fetchJobDetails = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/isler/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setJob(data);
      }
    } catch (error) {
      console.error('İş detayı alınamadı:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { label: 'Onay Bekliyor', className: 'bg-yellow-100 text-yellow-800' },
      'approved': { label: 'Onaylandı', className: 'bg-green-100 text-green-800' },
      'rejected': { label: 'Reddedildi', className: 'bg-red-100 text-red-800' },
      'needs_correction': { label: 'Düzeltme Gerekli', className: 'bg-orange-100 text-orange-800' },
      'completed': { label: 'Tamamlandı', className: 'bg-blue-100 text-blue-800' },
      'todo': { label: 'Yapılacak', className: 'bg-purple-100 text-purple-800' },
      'prepared': { label: 'Hazırlandı', className: 'bg-cyan-100 text-cyan-800' },
      'cancelled': { label: 'İptal Edildi', className: 'bg-red-100 text-red-800' }
    };
    const statusInfo = statusMap[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return <Badge className={statusInfo.className}>{statusInfo.label}</Badge>;
  };

  const getCompletedActionText = (action) => {
    const actionMap = {
      'giris_eklendi': 'Giriş Eklendi',
      'cikis_yapildi': 'Çıkış Yapıldı',
      'takip_eklendi': 'Takiplere Kaydedildi',
      'cek_kesildi': 'Çek Kesildi'
    };
    return actionMap[action] || action;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDownload = (belge) => {
    const fileSrc = belge.url || belge.data;
    const fileName = belge.name || belge.filename || 'belge';
    
    if (fileSrc?.startsWith('data:')) {
      try {
        const byteString = atob(fileSrc.split(',')[1]);
        const mimeType = fileSrc.match(/data:([^;]+);/)?.[1] || 'application/octet-stream';
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error('Download error:', e);
        window.open(fileSrc, '_blank');
      }
    } else if (fileSrc) {
      const link = document.createElement('a');
      link.href = fileSrc;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePreview = (belge) => {
    const fileSrc = belge.url || belge.data;
    const isImage = belge.type?.startsWith('image/') || belge.name?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
    
    if (isImage && fileSrc) {
      const modal = document.createElement('div');
      modal.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;z-index:9999;cursor:pointer';
      modal.onclick = () => modal.remove();
      const img = document.createElement('img');
      img.src = fileSrc;
      img.style.cssText = 'max-width:90%;max-height:90%;object-fit:contain;border-radius:8px';
      modal.appendChild(img);
      document.body.appendChild(modal);
    } else if (fileSrc) {
      window.open(fileSrc, '_blank');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-indigo-600" />
            İş Detayı
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[70vh] pr-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : job ? (
            <div className="space-y-4">
              {/* Konu ve Açıklama */}
              <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <h3 className="font-semibold text-indigo-900 text-lg">{job.konu}</h3>
                {job.aciklama && (
                  <p className="text-sm text-indigo-700 mt-2">{job.aciklama}</p>
                )}
              </div>

              {/* Durum ve Temel Bilgiler */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <span className="text-xs text-gray-500">Durum</span>
                  <div>{getStatusBadge(job.status)}</div>
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-gray-500">Şube</span>
                  <div className="flex items-center gap-1 text-sm">
                    <Building2 className="h-4 w-4 text-gray-400" />
                    {job.sube_adi || job.sube || '-'}
                  </div>
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-gray-500">Oluşturulma</span>
                  <div className="flex items-center gap-1 text-sm">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    {formatDate(job.created_at)}
                  </div>
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-gray-500">Oluşturan</span>
                  <div className="flex items-center gap-1 text-sm">
                    <User className="h-4 w-4 text-gray-400" />
                    {job.created_by_name || '-'}
                  </div>
                </div>
              </div>

              {/* Talep Bilgileri */}
              {job.talep_eden && (
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="text-xs text-purple-600 font-medium mb-2">Talep Bilgileri</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <span className="text-xs text-gray-500">Talep Eden</span>
                      <div className="flex items-center gap-1 text-sm font-medium text-purple-700">
                        <User className="h-4 w-4 text-purple-500" />
                        {job.talep_eden}
                      </div>
                    </div>
                    {job.amount && (
                      <div className="space-y-1">
                        <span className="text-xs text-gray-500">Tutar</span>
                        <div className="text-sm font-semibold text-purple-700">
                          {Number(job.amount).toLocaleString('tr-TR')} {job.currency || 'TRY'}
                        </div>
                      </div>
                    )}
                    {job.request_type && (
                      <div className="space-y-1">
                        <span className="text-xs text-gray-500">Talep Türü</span>
                        <div className="text-sm text-purple-700 capitalize">{job.request_type}</div>
                      </div>
                    )}
                    {job.priority && job.priority !== 'normal' && (
                      <div className="space-y-1">
                        <span className="text-xs text-gray-500">Öncelik</span>
                        <Badge className={job.priority === 'high' ? 'bg-red-100 text-red-700' : job.priority === 'urgent' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700'}>
                          {job.priority === 'high' ? 'Yüksek' : job.priority === 'urgent' ? 'Acil' : job.priority}
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* İşlemler ve Kayıtlar */}
              {(job.transactions?.length > 0 || job.created_records?.length > 0) && (
                <div className="border-2 border-blue-200 rounded-lg overflow-hidden">
                  <div className="bg-blue-50 px-4 py-2 border-b border-blue-200">
                    <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                      <ListChecks className="h-4 w-4" />
                      İşlemler ve Kayıtlar
                    </h4>
                  </div>
                  <div className="p-4 bg-white space-y-4">
                    {/* Yapılan İşlemler */}
                    {job.transactions?.length > 0 && (
                      <div>
                        <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <Receipt className="h-4 w-4 text-blue-500" />
                          Yapılan İşlemler ({job.transactions.length})
                        </h5>
                        <div className="space-y-2">
                          {job.transactions.map((tx, idx) => (
                            <div key={idx} className="bg-gray-50 border rounded-lg p-3">
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-2">
                                  <Badge className={
                                    tx.type === 'giris' ? 'bg-green-100 text-green-700' : 
                                    tx.type === 'cikis' ? 'bg-red-100 text-red-700' : 
                                    tx.type === 'cek' ? 'bg-purple-100 text-purple-700' :
                                    tx.type === 'alacak' ? 'bg-blue-100 text-blue-700' :
                                    tx.type === 'borc' ? 'bg-orange-100 text-orange-700' :
                                    'bg-gray-100 text-gray-700'
                                  }>
                                    {tx.type === 'giris' ? 'Giriş' : 
                                     tx.type === 'cikis' ? 'Çıkış' : 
                                     tx.type === 'cek' ? 'Çek' : 
                                     tx.type === 'alacak' ? 'Alacak' : 
                                     tx.type === 'borc' ? 'Borç' : tx.type}
                                  </Badge>
                                  <span className="font-bold text-lg text-gray-800">
                                    {parseFloat(tx.tutar || 0).toLocaleString('tr-TR')} ₺
                                  </span>
                                </div>
                                <span className="text-sm text-gray-500">
                                  {tx.tarih ? new Date(tx.tarih).toLocaleDateString('tr-TR') : '-'}
                                </span>
                              </div>
                              <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                                {tx.cari_hesap_adi && (
                                  <div>
                                    <span className="text-gray-500">Cari Hesap:</span>
                                    <span className="ml-1 font-medium">{tx.cari_hesap_adi}</span>
                                  </div>
                                )}
                                {tx.hesap_adi && (
                                  <div>
                                    <span className="text-gray-500">Hesap:</span>
                                    <span className="ml-1 font-medium">{tx.hesap_adi}</span>
                                  </div>
                                )}
                                {tx.vade_tarihi && (
                                  <div>
                                    <span className="text-gray-500">Vade:</span>
                                    <span className="ml-1 font-medium">{new Date(tx.vade_tarihi).toLocaleDateString('tr-TR')}</span>
                                  </div>
                                )}
                                {tx.created_by_name && (
                                  <div>
                                    <span className="text-gray-500">Ekleyen:</span>
                                    <span className="ml-1 font-medium">{tx.created_by_name}</span>
                                  </div>
                                )}
                              </div>
                              {tx.aciklama && (
                                <div className="mt-2 text-sm text-gray-600 bg-white p-2 rounded border">
                                  <span className="text-gray-500">Açıklama:</span> {tx.aciklama}
                                </div>
                              )}
                              {/* İşlem Belgeleri */}
                              {tx.belgeler && tx.belgeler.length > 0 && (
                                <div className="mt-2">
                                  <span className="text-xs text-gray-500 font-medium">Belgeler ({tx.belgeler.length})</span>
                                  <div className="mt-1 flex flex-wrap gap-2">
                                    {tx.belgeler.map((belge, belgeIdx) => (
                                      <div key={belgeIdx} className="flex items-center gap-1 bg-blue-50 border border-blue-200 px-2 py-1 rounded text-xs">
                                        <FileText className="h-3 w-3 text-blue-500" />
                                        <span className="max-w-[80px] truncate">{belge.name || `Belge ${belgeIdx + 1}`}</span>
                                        <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => handlePreview(belge)}>
                                          <Eye className="h-3 w-3" />
                                        </Button>
                                        <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => handleDownload(belge)}>
                                          <Download className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {/* Toplam Özet */}
                        <div className="mt-3 pt-3 border-t flex flex-wrap gap-3 text-sm">
                          {job.transactions.filter(t => t.type === 'giris').length > 0 && (
                            <div className="flex items-center gap-1 text-green-700 bg-green-50 px-3 py-1 rounded-full">
                              <ArrowDownCircle className="h-4 w-4" />
                              <span className="font-semibold">Giriş:</span>
                              <span>{job.transactions.filter(t => t.type === 'giris').reduce((a, b) => a + parseFloat(b.tutar || 0), 0).toLocaleString('tr-TR')} ₺</span>
                            </div>
                          )}
                          {job.transactions.filter(t => t.type === 'cikis').length > 0 && (
                            <div className="flex items-center gap-1 text-red-700 bg-red-50 px-3 py-1 rounded-full">
                              <ArrowUpCircle className="h-4 w-4" />
                              <span className="font-semibold">Çıkış:</span>
                              <span>{job.transactions.filter(t => t.type === 'cikis').reduce((a, b) => a + parseFloat(b.tutar || 0), 0).toLocaleString('tr-TR')} ₺</span>
                            </div>
                          )}
                          {job.transactions.filter(t => t.type === 'cek').length > 0 && (
                            <div className="flex items-center gap-1 text-purple-700 bg-purple-50 px-3 py-1 rounded-full">
                              <Receipt className="h-4 w-4" />
                              <span className="font-semibold">Çek:</span>
                              <span>{job.transactions.filter(t => t.type === 'cek').reduce((a, b) => a + parseFloat(b.tutar || 0), 0).toLocaleString('tr-TR')} ₺</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Oluşturulan Kayıtlar */}
                    {job.created_records?.length > 0 && (
                      <div>
                        <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          Oluşturulan Kayıtlar ({job.created_records.length})
                        </h5>
                        <div className="flex flex-wrap gap-2">
                          {job.created_records.map((record, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-green-50 border border-green-200 px-3 py-2 rounded-lg">
                              <Badge className={
                                record.type === 'giris' ? 'bg-green-100 text-green-700' : 
                                record.type === 'cikis' ? 'bg-red-100 text-red-700' : 
                                record.type === 'cek' ? 'bg-purple-100 text-purple-700' :
                                record.type === 'alacak' ? 'bg-blue-100 text-blue-700' :
                                'bg-orange-100 text-orange-700'
                              }>
                                {record.type === 'giris' ? 'Giriş' : 
                                 record.type === 'cikis' ? 'Çıkış' : 
                                 record.type === 'cek' ? 'Çek' : 
                                 record.type === 'alacak' ? 'Alacak' : 
                                 record.type === 'borc' ? 'Borç' : record.type}
                              </Badge>
                              <span className="font-mono font-semibold text-green-700">{record.seri_no || record.id}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Onay Geçmişi */}
              {(job.approved_at || job.prepared_at || job.board_approved_at) && (
                <div className="border-2 border-indigo-200 rounded-lg overflow-hidden">
                  <div className="bg-indigo-50 px-4 py-2 border-b border-indigo-200">
                    <h4 className="font-semibold text-indigo-800 flex items-center gap-2">
                      <ClipboardCheck className="h-4 w-4" />
                      Onay Geçmişi
                    </h4>
                  </div>
                  <div className="p-4 bg-white space-y-2 text-sm">
                    {job.approved_at && (
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="h-4 w-4" />
                        <span><strong>Onaylayan:</strong> {job.approved_by_name || '-'}</span>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-500">{formatDate(job.approved_at)}</span>
                      </div>
                    )}
                    {job.prepared_at && (
                      <div className="flex items-center gap-2 text-cyan-700">
                        <ClipboardCheck className="h-4 w-4" />
                        <span><strong>Hazırlayan:</strong> {job.prepared_by_name || '-'}</span>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-500">{formatDate(job.prepared_at)}</span>
                      </div>
                    )}
                    {job.board_approved_at && (
                      <div className="flex items-center gap-2 text-indigo-700">
                        <CheckCircle className="h-4 w-4" />
                        <span><strong>YK Onayı:</strong> {job.board_approved_by_name || '-'}</span>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-500">{formatDate(job.board_approved_at)}</span>
                      </div>
                    )}
                    {job.board_approval_note && (
                      <div className="mt-2 bg-indigo-50 p-2 rounded text-indigo-700">
                        <strong>YK Notu:</strong> {job.board_approval_note}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Düzeltme Geçmişi */}
              {job.corrections && job.corrections.length > 0 && (
                <div className="border-2 border-amber-200 rounded-lg overflow-hidden">
                  <div className="bg-amber-50 px-4 py-2 border-b border-amber-200">
                    <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Düzeltme Geçmişi ({job.corrections.length})
                    </h4>
                  </div>
                  <div className="p-4 bg-white space-y-2">
                    {job.corrections.map((correction, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border ${correction.tip === 'duzeltme_gonderildi' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                        <div className="flex items-center gap-2 text-sm">
                          {correction.tip === 'duzeltme_gonderildi' ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                          )}
                          <span className="font-medium">
                            {correction.tip === 'duzeltme_gonderildi' ? 'Düzeltme Gönderildi' : 'Düzeltme Talebi'}
                          </span>
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-500">{formatDate(correction.created_at)}</span>
                        </div>
                        {correction.mesaj && (
                          <p className="mt-2 text-sm text-gray-700">{correction.mesaj}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tamamlanan Aksiyon */}
              {job.completed_action && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-xs text-green-600 font-medium">Yapılan İşlem:</span>
                    <Badge className="bg-green-100 text-green-800">
                      {getCompletedActionText(job.completed_action)}
                    </Badge>
                  </div>
                  {job.completed_at && (
                    <p className="text-xs text-green-600 mt-1">
                      {formatDate(job.completed_at)}
                    </p>
                  )}
                </div>
              )}

              {/* İş Belgeleri */}
              {job.belgeler && job.belgeler.length > 0 && (
                <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      İş Belgeleri ({job.belgeler.length})
                    </h4>
                  </div>
                  <div className="p-4 bg-white">
                    <div className="flex flex-wrap gap-2">
                      {job.belgeler.map((belge, idx) => {
                        const fileSrc = belge.url || belge.data;
                        const fileName = belge.name || belge.filename || `Belge ${idx + 1}`;
                        const isImage = belge.type?.startsWith('image/') || fileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                        
                        return (
                          <div
                            key={idx}
                            className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg text-sm border"
                          >
                            <FileText className={`h-4 w-4 ${isImage ? 'text-green-500' : 'text-blue-500'}`} />
                            <span className="max-w-[150px] truncate font-medium" title={fileName}>{fileName}</span>
                            {fileSrc && (
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-blue-600" onClick={() => handlePreview(belge)} title="Önizle">
                                  <Eye className="h-3 w-3" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-green-600" onClick={() => handleDownload(belge)} title="İndir">
                                  <Download className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              İş bilgisi bulunamadı
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default JobDetailPopup;
